import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './DocumentSelectionModal.css';
import '../primitives/Button.css';
import MyDocumentsCard from './MyDocumentsCard';
import FileUpload from '../FileUpload';

const DocumentSelectionModal = ({
  isOpen,
  onClose,
  onConnectDocuments,
  selectedDocuments = [],
  onToggleDocument,
  requiredStatusIds = [],
  documentTypeId = null,
  title = "Select Documents",
  endeavorId = null  // Optional: scope uploads to a specific course
}) => {
  const [showUpload, setShowUpload] = useState(false);
  // Local staging state — changes only commit to parent on "Connect"
  const [localSelected, setLocalSelected] = useState([...selectedDocuments]);

  // Sync local state when the modal opens (or when parent selection changes while open)
  useEffect(() => {
    if (isOpen) {
      setLocalSelected([...selectedDocuments]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) {
      setShowUpload(false); // Reset upload view when modal closes
      return;
    }
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showUpload) {
          setShowUpload(false); // Go back to selection view on Escape
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, showUpload]);

  if (!isOpen) return null;

  const handleLocalToggle = (docId) => {
    setLocalSelected(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleConnect = () => {
    onConnectDocuments(localSelected);
    onClose();
  };

  const handleUploadComplete = (data) => {
    // Auto-select the newly uploaded document in local staging state
    if (data?.documentId) {
      const docId = String(data.documentId);
      // Only add if not already selected
      if (!localSelected.includes(docId)) {
        setLocalSelected(prev => [...prev, docId]);
      }
    }
    // Return to selection view after successful upload
    setShowUpload(false);
    // Documents will auto-refresh via documents:changed event
  };

  const modalContent = (
    <div className="document-modal-overlay" onClick={onClose}>
      <div className="document-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21.44 11.05L12.25 20.54C11.12 21.67 9.59 22.22 8.06 22.22C6.53 22.22 5 21.67 3.87 20.54C2.74 19.41 2.19 17.88 2.19 16.35C2.19 14.82 2.74 13.29 3.87 12.16L13.06 2.97C13.85 2.18 14.93 1.75 16.06 1.75C17.19 1.75 18.27 2.18 19.06 2.97C19.85 3.76 20.28 4.84 20.28 5.97C20.28 7.1 19.85 8.18 19.06 8.97L9.87 18.16C9.48 18.55 8.93 18.75 8.37 18.75C7.81 18.75 7.26 18.55 6.87 18.16C6.48 17.77 6.28 17.22 6.28 16.66C6.28 16.1 6.48 15.55 6.87 15.16L15.06 6.97" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>{title}</h3>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {!showUpload && (
              <button
                className="ui-btn ui-btn--secondary"
                onClick={() => setShowUpload(true)}
              >Upload New Document</button>
            )}
            <button className="close-button ui-btn ui-btn--ghost" onClick={onClose} title="Close modal">✕</button>
          </div>
        </div>

        {showUpload ? (
          <div className="documents-section" style={{ padding: '1rem' }}>
            <FileUpload 
              onUploadComplete={handleUploadComplete}
              endeavorId={endeavorId}
              scoutIQMode={false}
            />
          </div>
        ) : (
          <>
            <div className="documents-section" style={{ padding: 0 }}>
              <MyDocumentsCard
                selectionMode
                selectedDocumentIds={localSelected.map(String)}
                onToggleSelect={(id) => handleLocalToggle(String(id))}
                requiredStatusIds={requiredStatusIds}
                pinnedDocumentTypeId={documentTypeId || null}
                disableNavigationInSelection
              />
            </div>
          </>
        )}

        <div className="modal-footer">
          <button className="ui-btn ui-btn--neutral" onClick={onClose}>Cancel</button>
          {!showUpload && (
            <button
              className="ui-btn ui-btn--primary"
              onClick={handleConnect}
            >Connect ({localSelected.length})</button>
          )}
        </div>
      </div>
    </div>
  );

  // Render modal to document.body using portal for center-screen positioning
  return createPortal(modalContent, document.body);
};

export default DocumentSelectionModal;